// apps/admin/src/lib/error-boundary.tsx
//
// Phase 8.3 Wave 6 Task 51 — Global ErrorBoundaryAutoReport for the admin
// SPA. Wraps the app shell; any React render-time error caught here is
// POSTed to /api/v1/admin/bug-tracker/auto-report (anonymous payload OK)
// before the fallback UI renders.
//
// Per CONTEXT.md
//   • Bug Tracker SA-only Kanban surface; this boundary is the
//                ingestion source for the "auto-reported" column.
//   • Auto-report ingestion from FE error boundaries. Lower
//                rate limit, optional auth. Dedup happens BE-side via
//                stack_trace_hash.
//
// Per INTERFACE-STANDARDS.md § error-shape — the auto-report endpoint
// returns the canonical `{ ok, data }` envelope; on failure we silently
// swallow the error (auto-report itself failing must not loop back into a
// new render error or block the fallback UI).
//
// Implementation notes:
//   - React 19 still requires a class component for boundaries (no hook
//     equivalent yet). Functional wrapper only forwards children.
//   - We post AT MOST ONCE per boundary mount to avoid runaway loops when
//     the same render error fires repeatedly. The BE also dedups by
//     stack_trace_hash, but FE de-dup saves the round-trip.
//   - `import.meta.env.DEV` short-circuits the POST so dev console errors
//     don't pollute the production bug tracker.
//   - Anonymous payload OK: the BE handler decodes the JWT cookie best-
//     effort and falls back to reporter='system'. We do NOT depend on
//     apiClient (which would import the auth refresh chain — the
//     boundary may fire BEFORE auth init). A raw fetch is enough; on
//     network failure we swallow.
//
// vg-binding: CONTEXT:decisions=P8.D-126,P8.D-164
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: CONTEXT:decisions=P999.11.D-05,P999.11.D-14,P999.11.D-16,P999.11.D-18 — dark-mode: fatal card neutral fill migrated to pw elevated token (Task 20)

import { Component, type ErrorInfo, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Payload shape — mirrors API-CONTRACTS.md §9 PostAutoReportRequest.
// ---------------------------------------------------------------------------

interface AutoReportPayload {
  title: string;
  stack_trace: string;
  source_url: string;
  user_agent: string;
  category: 'UI' | 'Logic' | 'Performance' | 'Data' | 'Security';
}

function buildPayload(error: Error, info: ErrorInfo): AutoReportPayload {
  // Title: first line of error message, capped at 200 chars per BE Zod schema.
  const raw = error.message || error.name || 'Unknown error';
  const messageHead = (raw.split('\n')[0] ?? raw).slice(0, 200);

  // Stack: prefer error.stack (V8 detailed format); fall back to
  // componentStack from React. Truncate at 20000 chars per BE Zod schema.
  const stackParts: string[] = [];
  if (error.stack) stackParts.push(error.stack);
  if (info.componentStack) {
    stackParts.push('--- React component stack ---');
    stackParts.push(info.componentStack);
  }
  const stackTrace = stackParts.join('\n').slice(0, 20000);

  const sourceUrl =
    typeof window !== 'undefined' && window.location
      ? window.location.href.slice(0, 500)
      : '';
  const userAgent =
    typeof navigator !== 'undefined' && navigator.userAgent
      ? navigator.userAgent.slice(0, 500)
      : '';

  return {
    title: `[Auto] ${messageHead}`,
    stack_trace: stackTrace,
    source_url: sourceUrl,
    user_agent: userAgent,
    category: 'UI',
  };
}

/**
 * Best-effort POST to the auto-report endpoint. Bypasses `apiClient` so the
 * boundary works even before the auth-refresh chain is wired (the BE
 * accepts anonymous payloads). Failures are swallowed.
 */
async function postAutoReport(payload: AutoReportPayload): Promise<void> {
  if (typeof fetch !== 'function') return;
  try {
    // INTERFACE-STANDARDS.md correlation_id_header: include X-Request-Id
    // when crypto.randomUUID is available. Optional — BE generates its own
    // when missing.
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      headers['X-Request-Id'] = crypto.randomUUID();
    }
    const baseUrl =
      (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(
        /\/$/,
        '',
      ) ?? '';
    await fetch(`${baseUrl}/api/v1/admin/bug-tracker/auto-report`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    // Auto-report failure is non-fatal; never propagate.
  }
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ErrorBoundaryAutoReportProps {
  /** App tree wrapped by the boundary. */
  children: ReactNode;
  /**
   * Render-prop fallback. Receives the caught error + a reset callback
   * that clears the boundary's error state (e.g. for a "Reload" button).
   * Defaults to a minimal in-place card so the boundary is drop-in.
   */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
  /**
   * Set to `false` to skip the POST (tests + Storybook). Defaults to
   * `true` in prod builds, `false` under `import.meta.env.DEV` to keep
   * the dev console clean.
   */
  autoReport?: boolean;
}

interface ErrorBoundaryAutoReportState {
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Class component (React 19 still requires class for componentDidCatch)
// ---------------------------------------------------------------------------

export class ErrorBoundaryAutoReport extends Component<
  ErrorBoundaryAutoReportProps,
  ErrorBoundaryAutoReportState
> {
  /** Guard against runaway POST loops if the boundary re-mounts repeatedly. */
  private reported = false;

  constructor(props: ErrorBoundaryAutoReportProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryAutoReportState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Defensive — surface the error in the console regardless (test runners
    // and devtools rely on this for noisy stack traces).
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundaryAutoReport]', error, info);

    const shouldReport =
      this.props.autoReport ?? !import.meta.env.DEV;
    if (!shouldReport || this.reported) return;
    this.reported = true;
    const payload = buildPayload(error, info);
    void postAutoReport(payload);
  }

  reset = (): void => {
    this.reported = false;
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    // Default fallback — minimal full-screen card. Matches the
    // InternalServerErrorPage palette so users get a familiar "something
    // broke" surface without forcing a hard reload.
    return (
      <main
        role="alert"
        aria-live="assertive"
        className="flex min-h-screen items-center justify-center bg-pw-surface p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="flex max-w-md flex-col gap-4 rounded-lg border border-pw-border bg-pw-bg-elevated p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-pw-text">
            Đã có lỗi xảy ra
          </h1>
          <p className="text-sm text-pw-text-muted">
            Trang gặp lỗi không mong muốn (unexpected error). Sự cố đã được
            tự động gửi tới đội kỹ thuật để xử lý.
          </p>
          <details className="rounded-md border border-pw-border bg-pw-surface p-3 text-xs text-pw-text-muted">
            <summary className="cursor-pointer font-medium text-pw-text">
              Chi tiết kỹ thuật
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
              {error.name}: {error.message}
            </pre>
          </details>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex h-9 items-center justify-center rounded-md bg-pw-primary px-4 text-sm font-medium text-white transition-colors hover:bg-pw-primary-hover"
              data-testid="error-boundary-retry"
            >
              Thử lại
            </button>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== 'undefined') window.location.reload();
              }}
              className="inline-flex h-9 items-center justify-center rounded-md border border-pw-border bg-pw-bg px-4 text-sm font-medium text-pw-text transition-colors hover:bg-pw-surface"
              data-testid="error-boundary-reload"
            >
              Tải lại trang
            </button>
          </div>
        </div>
      </main>
    );
  }
}

export default ErrorBoundaryAutoReport;
